import { Avatar } from '@/art';
import type { EquippedCosmetic } from '@shared/protocol';
import { useT } from '@/i18n/useI18n';
import { isImageAssetUrl, resolveAssetUrl } from '@/shop/assetUrl';

interface LaneHeaderProps {
  side: 'left' | 'right';
  color: 'red' | 'blue';
  name: string;
  mmr: number;
  step: number;
  /** Real per-match total — comes from server `match.found` payload, may
   * differ from the build-time STEPS_TOTAL if the admin tweaked it. */
  total: number;
  width: number;
  /** Equipped avatarFrame cosmetic — wraps the avatar with a colored border. */
  avatarFrame?: EquippedCosmetic | null;
  /** Uploaded avatar photo, if any. */
  photoUrl?: string | null;
  /** Equipped character idle PNG — avatar fallback when no photo. */
  characterUrl?: string | null;
  /** Clan badge — emoji or /uploads emblem + tag, shown next to the name. */
  clanTag?: string | null;
  clanEmblem?: string | null;
}

export function LaneHeader({
  side,
  color,
  name,
  mmr,
  step,
  total,
  width,
  avatarFrame,
  photoUrl,
  characterUrl,
  clanTag,
  clanEmblem,
}: LaneHeaderProps) {
  const t = useT();
  const rim = color === 'red' ? 'var(--c-red)' : 'var(--c-blue)';
  const tagColor = side === 'left' ? 'var(--c-cyan)' : 'var(--c-red)';
  const tagLabel = side === 'left' ? t('match.you') : t('match.rival');

  return (
    <div
      style={{
        width,
        height: 44,
        padding: '4px 6px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexDirection: side === 'right' ? 'row-reverse' : 'row',
        background: 'rgba(0,0,0,0.35)',
        border: `1px solid ${rim}40`,
        borderRadius: '10px 10px 0 0',
        borderBottom: 'none',
        position: 'relative',
        zIndex: 5,
      }}
    >
      <AvatarWithFrame color={color} frame={avatarFrame ?? null} photoUrl={photoUrl} characterUrl={characterUrl} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: side === 'right' ? 'right' : 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 4,
            justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 7,
              letterSpacing: '0.12em',
              color: tagColor,
            }}
          >
            {tagLabel}
          </span>
          <span className="display" style={{ fontSize: 11, letterSpacing: '0.04em' }}>
            {name}
          </span>
          {clanTag && <ClanBadge tag={clanTag} emblem={clanEmblem ?? null} />}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            marginTop: 1,
            justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 14,
              color: rim,
              fontWeight: 700,
              textShadow: `0 0 8px ${rim}`,
            }}
          >
            {step}
          </span>
          <span className="mono" style={{ fontSize: 8, color: 'var(--ink-dim)' }}>
            /{total}
          </span>
          <span
            className="mono"
            style={{ fontSize: 8, color: 'var(--ink-dim)', marginLeft: 4 }}
          >
            {mmr}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Compact clan badge: emblem (emoji or image) + [tag]. */
function ClanBadge({ tag, emblem }: { tag: string; emblem: string | null }) {
  const isImg = isImageAssetUrl(emblem);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, opacity: 0.92 }}>
      {isImg
        ? <img src={resolveAssetUrl(emblem) ?? ''} alt="" style={{ width: 12, height: 12, objectFit: 'contain' }} />
        : <span style={{ fontSize: 11, lineHeight: 1 }}>{emblem || '🛡️'}</span>}
      <span className="mono" style={{ fontSize: 8, color: 'var(--ink-dim)' }}>[{tag}]</span>
    </span>
  );
}

function AvatarWithFrame({
  color,
  frame,
  photoUrl,
  characterUrl,
}: {
  color: 'red' | 'blue';
  frame: EquippedCosmetic | null;
  photoUrl?: string | null;
  characterUrl?: string | null;
}) {
  // Delegate frame rendering to Avatar — it handles the image overlay as well
  // as the CSS border + glow ring uniformly.
  const spec = frame?.data
    ? {
        border: (frame.data.border as string | undefined) ?? null,
        glow: (frame.data.glow as string | undefined) ?? null,
        imageUrl: (frame.data.imageUrl as string | undefined) ?? null,
      }
    : null;
  return (
    <Avatar
      color={color}
      size={32}
      ring
      frame={spec}
      photoUrl={photoUrl ?? undefined}
      characterUrl={characterUrl}
    />
  );
}
